import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, CheckCircle, Circle, Calendar, User } from 'lucide-react';
import { tasksAPI } from '../services/api';
import Modal from '../components/Modal';
import { useAuth } from '../context/AuthContext';
import CXTaskListings from './tasks/CXTaskListings';
import TaskCommandCenter from './tasks/TaskCommandCenter';
import QCTasks from './tasks/QCTasks';
import CXLeaderTasks from './tasks/CXLeaderTasks';
import OnboardingFormTasks from './tasks/OnboardingFormTasks';
import DateRangeFilter from '../components/DateRangeFilter';
import { TaskSearchBar } from '../components/tasks/TableControls';

const Tasks = () => {
  const { user } = useAuth();
  // iter266 — Multi-role Tasks page. When the logged-in user has more
  // than one role (see iter264), the Tasks page composes one section per
  // role that has its own tasks surface. Single-role users continue to
  // see the exact same component they saw before (rendered directly, no
  // header wrapper) so this change is transparent for them.
  const roles = Array.isArray(user?.roles) && user.roles.length
    ? user.roles.map(r => (r || '').toLowerCase()).filter(Boolean)
    : [(user?.role || '').toLowerCase()].filter(Boolean);

  // Admin / executive stay on the unified command center as before —
  // they are superusers whose command view already covers everything.
  if (roles.includes('admin') || roles.includes('executive')) {
    return <TaskCommandCenter />;
  }

  // Role → section descriptor. Order preserved from this array is the
  // order shown on the multi-role stacked view.
  const SECTION_REGISTRY = [
    { role: 'cx_leader',        title: 'CX Leader Tasks',       Component: CXLeaderTasks },
    { role: 'qc',               title: 'QC Tasks',              Component: QCTasks },
    { role: 'cx',               title: 'CX Tasks',              Component: CXTaskListings },
    { role: 'onboarding_form',  title: 'Onboarding Form Tasks', Component: OnboardingFormTasks },
  ];
  const active = SECTION_REGISTRY.filter(s => roles.includes(s.role));

  if (active.length === 0) return <GenericTasks />;
  if (active.length === 1) {
    // Preserve the pre-iter266 render (no header, no extra wrapper) so
    // single-role users get an identical experience.
    const Only = active[0].Component;
    return <Only />;
  }

  // Multi-role user — stack each section with a role-labeled header.
  return (
    <div className="space-y-8" data-testid="tasks-multi-role">
      {active.map(({ role, title, Component }) => (
        <section
          key={role}
          className="space-y-3"
          data-testid={`tasks-section-${role}`}
        >
          <header className="flex items-center gap-3 border-l-4 border-teal-500 pl-3 py-1">
            <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
            <span className="text-[11px] uppercase tracking-wider text-gray-400">role · {role}</span>
          </header>
          <Component />
        </section>
      ))}
    </div>
  );
};

const GenericTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState({});
  const [loading, setLoading] = useState(true);
  // Item 9 — Tasks date filter has NO default (user picks). Filters the
  // personal task list client-side by due date.
  const [range, setRange] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  // Property/task search — filters only the currently visible task list.
  const [search, setSearch] = useState('');

  useEffect(() => {
    refreshTasks();
  }, []);

  const refreshTasks = async () => {
    try {
      setLoading(true);
      const response = await tasksAPI.getAll();
      const data = response.data || [];
      // Sort: pending first, then completed
      data.sort((a, b) => {
        if (a.status === b.status) return 0;
        return a.status === 'Completed' ? 1 : -1;
      });
      setTasks(data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (task) => {
    const newStatus = task.status === 'Completed' ? 'Pending' : 'Completed';
    try {
      await tasksAPI.update(task.id, { status: newStatus });
      refreshTasks();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this task?')) {
      try {
        await tasksAPI.delete(id);
        refreshTasks();
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
  };

  const handleEdit = (task) => {
    setCurrentTask(task);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const taskData = {
      ...currentTask,
      status: currentTask.status || 'Pending',
      priority: currentTask.priority || 'Medium',
      assigned_to: currentTask.assigned_to || 'Unassigned'
    };

    try {
      if (currentTask.id) {
        await tasksAPI.update(currentTask.id, taskData);
      } else {
        await tasksAPI.create(taskData);
      }
      setIsModalOpen(false);
      setCurrentTask({});
      refreshTasks();
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  // Item 9 — client-side date range filter (reference: due date).
  const rangeBounds = () => {
    const now = new Date();
    if (range === 'this_week') { const d = new Date(now); const day = (d.getDay() + 6) % 7; d.setDate(d.getDate() - day); d.setHours(0, 0, 0, 0); return [d, null]; }
    if (range === 'this_month') return [new Date(now.getFullYear(), now.getMonth(), 1), null];
    if (range === 'last_6_months') { const d = new Date(now); d.setDate(d.getDate() - 183); return [d, null]; }
    if (range === 'this_year') return [new Date(now.getFullYear(), 0, 1), null];
    if (range === 'custom') return [customFrom ? new Date(customFrom) : null, customTo ? new Date(`${customTo}T23:59:59`) : null];
    return [null, null];
  };
  const [boundStart, boundEnd] = rangeBounds();
  const filteredTasks = tasks.filter((t) => {
    if (range === 'all') return true;
    const ref = t.due_date || t.created_at;
    if (!ref) return false;
    const d = new Date(ref);
    if (boundStart && d < boundStart) return false;
    if (boundEnd && d > boundEnd) return false;
    return true;
  });
  // Apply property/task search on top of the date filter (visible list only).
  const sq = search.trim().toLowerCase();
  const visibleTasks = sq
    ? filteredTasks.filter((t) => [t.title, t.assigned_to].some((v) => String(v || '').toLowerCase().includes(sq)))
    : filteredTasks;

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <h2 className="text-xl font-bold text-gray-800">My Tasks</h2>
        <button 
          onClick={() => { setCurrentTask({}); setIsModalOpen(true); }}
          className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors shadow-sm"
          data-testid="add-task-btn"
        >
          <Plus size={20} className="mr-2" />
          Add Task
        </button>
      </div>

      <DateRangeFilter value={range} onChange={setRange} customFrom={customFrom} customTo={customTo}
        onCustomFrom={setCustomFrom} onCustomTo={setCustomTo} testId="generic-tasks-date-filter" />

      <TaskSearchBar query={search} setQuery={setSearch} placeholder="Search tasks by title…" testId="generic-tasks-search" />

      <div className="space-y-3">
        {visibleTasks.map(task => (
            <div 
              key={task.id} 
              className={`bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex items-center justify-between group transition-all ${
                task.status === 'Completed' ? 'opacity-60 bg-gray-50' : 'hover:shadow-md'
              }`}
              data-testid={`task-${task.id}`}
            >
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => handleToggleStatus(task)}
                  className={`p-1 rounded-full transition-colors ${
                    task.status === 'Completed' ? 'text-green-500 hover:text-green-600' : 'text-gray-300 hover:text-teal-500'
                  }`}
                  data-testid={`toggle-task-${task.id}`}
                >
                  {task.status === 'Completed' ? <CheckCircle size={24} /> : <Circle size={24} />}
                </button>
                <div>
                  <h3 className={`font-semibold text-gray-900 ${task.status === 'Completed' ? 'line-through text-gray-500' : ''}`}>
                    {task.title}
                  </h3>
                  <div className="flex items-center mt-2 space-x-4 text-xs text-gray-500">
                    <span className="flex items-center">
                      <Calendar size={12} className="mr-1" />
                      {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}
                    </span>
                    <span className="flex items-center">
                      <User size={12} className="mr-1" />
                      {task.assigned_to || 'Unassigned'}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full font-medium ${
                      task.priority === 'High' ? 'bg-red-100 text-red-700' : 
                      task.priority === 'Medium' ? 'bg-amber-100 text-amber-700' : 
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {task.priority || 'Medium'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button 
                  onClick={() => handleEdit(task)}
                  className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg"
                  data-testid={`edit-task-${task.id}`}
                >
                  <Edit2 size={18} />
                </button>
                <button 
                  onClick={() => handleDelete(task.id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  data-testid={`delete-task-${task.id}`}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        {visibleTasks.length === 0 && (
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 text-center text-gray-500">
            <p>No tasks yet. Create your first task!</p>
          </div>
        )}
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title={currentTask.id ? "Edit Task" : "New Task"}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input 
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-teal-500"
              value={currentTask.title || ''}
              onChange={e => setCurrentTask({...currentTask, title: e.target.value})}
              data-testid="task-title-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input 
                required
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-teal-500"
                value={currentTask.due_date ? currentTask.due_date.split('T')[0] : ''}
                onChange={e => setCurrentTask({...currentTask, due_date: e.target.value})}
                data-testid="task-due-date-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                value={currentTask.priority || 'Medium'}
                onChange={e => setCurrentTask({...currentTask, priority: e.target.value})}
                data-testid="task-priority-select"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
          </div>
          <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
             <input 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-teal-500"
                value={currentTask.assigned_to || ''}
                onChange={e => setCurrentTask({...currentTask, assigned_to: e.target.value})}
                placeholder="e.g. Employee Name"
                data-testid="task-assigned-to-input"
             />
          </div>
          <div className="pt-4 flex justify-end space-x-3">
             <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
             <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700" data-testid="save-task-btn">Save Task</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Tasks;
